/**
 * PaidPage — single component that renders all 10 paid-member pages
 * (Functions, Catering, Gallery, FAQ, Reviews, Press, Loyalty, Gift Cards,
 * Specials, Blog) driven off the `pageType` prop.
 *
 * Each page reads optional entity overrides (e.g. restaurant.functions_html)
 * but falls back to a clean default template so every Platinum venue ships
 * with all 10 pages live on day one — owners can override anytime.
 *
 * Gating: non-paid venues see a "Claim this listing to unlock" placeholder.
 */
import { Link, useOutletContext } from "react-router-dom";
import {
  Star,
  Sparkles,
  CalendarDays,
  Camera,
  Heart,
  Gift,
  HelpCircle,
  Megaphone,
  Newspaper,
  PartyPopper,
  Utensils,
  ArrowRight,
  Quote,
} from "lucide-react";

const PAGE_META = {
  functions: { title: "Functions & Private Events", icon: PartyPopper, blurb: "Host birthdays, corporate dinners, hen's days or wedding receptions." },
  catering:  { title: "Catering", icon: Utensils, blurb: "Off-site catering & platters for offices, schools and home gatherings." },
  gallery:   { title: "Gallery", icon: Camera, blurb: "A look inside the studio, the team, and the work." },
  faq:       { title: "Frequently Asked", icon: HelpCircle, blurb: "Everything you might want to know — answered." },
  reviews:   { title: "Reviews", icon: Star, blurb: "What our community says." },
  press:     { title: "In the Press", icon: Newspaper, blurb: "Selected media mentions, awards and features." },
  loyalty:   { title: "Loyalty Programme", icon: Heart, blurb: "Earn points on every visit. Redeem in-studio or online." },
  "gift-cards": { title: "Gift Cards", icon: Gift, blurb: "The perfect gift for someone who deserves a treat." },
  specials:  { title: "Specials & Promotions", icon: Megaphone, blurb: "Current offers, weekday deals and seasonal packages." },
  blog:      { title: "Blog & News", icon: CalendarDays, blurb: "Latest stories from the studio." },
  aftercare: { title: "Aftercare & Information", icon: HelpCircle, blurb: "Everything you need to know about caring for your treatment after you leave the studio." },
};

const FAQ_DEFAULT = [
  { q: "Do I need to book or can I walk in?", a: "We strongly recommend booking — appointments fill quickly, especially on weekends. You can book online 24/7 via the Book Now button at the top." },
  { q: "What's your cancellation policy?", a: "Free cancellation up to 24 hours prior. Cancellations within 24 hours may incur a 50% fee to cover the time held." },
  { q: "How long do treatments last?", a: "Most services run 30-120 minutes — exact duration is shown next to each service on the Products page." },
  { q: "Do you offer aftercare advice?", a: "Yes — every appointment includes a tailored aftercare brief and a take-home info card." },
  { q: "What forms of payment do you accept?", a: "Card (tap, chip, swipe), Apple Pay, Google Pay, and gift cards. We don't accept cash at this location." },
];

const REVIEWS_DEFAULT = [
  { author: "Sarah M.", rating: 5, when: "2 weeks ago", text: "Absolutely flawless. The staff are warm, the studio spotless, and the results lasted longer than I expected. Already rebooked." },
  { author: "Jess T.", rating: 5, when: "1 month ago", text: "Best in Brisbane. I drive 40 minutes to come here and it's worth every km. The detail and care is on another level." },
  { author: "Olivia R.", rating: 5, when: "1 month ago", text: "Kate took the time to actually listen to what I wanted instead of pushing a default look. Loved the result." },
  { author: "Maddie K.", rating: 4, when: "2 months ago", text: "Lovely studio, easy parking nearby. Booking system worked perfectly. Will be back." },
];

const SPECIALS_DEFAULT = [
  { name: "Tuesday Lash Refill", price: "$59", was: "$75", desc: "Mid-week refill discount — book any Tuesday." },
  { name: "Brow + Lash Combo", price: "$120", was: "$150", desc: "Lamination + lift, save $30 when booked together." },
  { name: "Bridal Trial Package", price: "$220", desc: "Trial run + the big day — includes touch-up the morning of." },
];

const STAR = (filled = true) => (
  <Star className={`w-3.5 h-3.5 ${filled ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
);

function ReviewsBody({ restaurant }) {
  const reviews = (restaurant.reviews_list && restaurant.reviews_list.length) ? restaurant.reviews_list : REVIEWS_DEFAULT;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {reviews.map((r, i) => (
        <div
          key={i}
          className="p-5 rounded-2xl border bg-white"
          style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}
          data-testid={`review-card-${i}`}
        >
          <Quote className="w-5 h-5 text-stone-300 mb-2" />
          <p className="text-[15px] text-stone-800 leading-relaxed">{r.text}</p>
          <div className="mt-3 flex items-center justify-between">
            <div className="text-[13px] font-semibold text-stone-800">{r.author}</div>
            <div className="flex items-center gap-0.5">{Array.from({ length: 5 }).map((_, j) => STAR(j < r.rating))}</div>
          </div>
          <div className="text-[11px] text-stone-400 mt-0.5">{r.when}</div>
        </div>
      ))}
    </div>
  );
}

function FaqBody({ restaurant }) {
  const items = (restaurant.faq_list && restaurant.faq_list.length) ? restaurant.faq_list : FAQ_DEFAULT;
  return (
    <div className="divide-y divide-stone-200 border rounded-2xl bg-white" style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}>
      {items.map((it, i) => (
        <details key={i} className="group" data-testid={`faq-item-${i}`}>
          <summary className="cursor-pointer list-none flex items-center justify-between px-5 py-4 hover:bg-stone-50">
            <span className="font-semibold text-stone-900">{it.q}</span>
            <span className="text-stone-400 group-open:rotate-45 transition-transform">+</span>
          </summary>
          <div className="px-5 pb-4 text-[14px] text-stone-700 leading-relaxed">{it.a}</div>
        </details>
      ))}
    </div>
  );
}

function GalleryBody({ restaurant }) {
  const imgs =
    restaurant.gallery_images && restaurant.gallery_images.length
      ? restaurant.gallery_images
      : [restaurant.hero || restaurant.hero_image_url].filter(Boolean);
  if (!imgs.length) {
    return <p className="text-stone-500 text-[14px]">Gallery coming soon.</p>;
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="gallery-grid">
      {imgs.map((src, i) => (
        <div key={i} className="aspect-square overflow-hidden rounded-xl bg-stone-100">
          <img
            src={src}
            alt={`Gallery ${i + 1}`}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          />
        </div>
      ))}
    </div>
  );
}

function SpecialsBody({ restaurant }) {
  const items = (restaurant.specials_list && restaurant.specials_list.length) ? restaurant.specials_list : SPECIALS_DEFAULT;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((s, i) => (
        <div key={i} className="p-5 rounded-2xl border bg-gradient-to-br from-amber-50 to-white" style={{ borderColor: "var(--tpl-line, #e7e5e4)" }} data-testid={`special-${i}`}>
          <div className="flex items-baseline justify-between mb-2">
            <div className="font-bold text-stone-900">{s.name}</div>
            <div className="text-right">
              <div className="font-bold text-amber-700 tabular-nums">{s.price}</div>
              {s.was && <div className="text-[11px] text-stone-400 line-through tabular-nums">{s.was}</div>}
            </div>
          </div>
          <p className="text-[13px] text-stone-600">{s.desc}</p>
        </div>
      ))}
    </div>
  );
}

function GiftCardsBody({ restaurant, slug }) {
  const tiers = [25, 50, 100, 150, 200];
  return (
    <div className="space-y-6">
      <p className="text-stone-700 text-[15px] max-w-2xl">
        Treat someone you love to a {restaurant.name} gift card. Choose any amount below
        — delivered instantly by email, ready to redeem online or in-studio.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {tiers.map((t) => (
          <button
            key={t}
            data-testid={`gift-tier-${t}`}
            className="aspect-square rounded-2xl border-2 border-stone-200 hover:border-amber-500 transition-colors flex flex-col items-center justify-center font-bold text-stone-900 hover:bg-amber-50"
          >
            <Gift className="w-5 h-5 mb-1 text-amber-600" />
            <span className="text-2xl tabular-nums">${t}</span>
          </button>
        ))}
      </div>
      <Link
        to={`/r/${slug}/contact`}
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-stone-900 text-white text-[13px] font-bold hover:bg-stone-700"
      >
        Get a custom-value gift card <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

function LoyaltyBody({ restaurant }) {
  const tiers = [
    { name: "Bronze", spend: "0", perks: ["10% off your 6th visit", "Birthday treat"] },
    { name: "Silver", spend: "500/yr", perks: ["15% off refills", "Priority booking window", "Birthday treat"] },
    { name: "Gold",   spend: "1,500/yr", perks: ["20% off all services", "Free brow tint every visit", "VIP-only late-night events", "Birthday treat"] },
  ];
  return (
    <div className="space-y-6">
      <p className="text-stone-700 text-[15px] max-w-2xl">
        Earn 1 point per $1 spent at {restaurant.name}. Tiers reset annually on your
        anniversary. Points never expire on Silver+.
      </p>
      <div className="grid sm:grid-cols-3 gap-4">
        {tiers.map((t) => (
          <div key={t.name} className="p-5 rounded-2xl border bg-white" style={{ borderColor: "var(--tpl-line, #e7e5e4)" }} data-testid={`loyalty-tier-${t.name}`}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="font-bold text-stone-900">{t.name}</span>
              <span className="ml-auto text-[11px] text-stone-400">${t.spend}</span>
            </div>
            <ul className="space-y-1.5 text-[13px] text-stone-700">
              {t.perks.map((p, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-amber-500 mt-1">•</span>{p}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function AftercareBody({ restaurant }) {
  const sections = restaurant.aftercare_sections || [];
  if (!sections.length) {
    return (
      <p className="text-stone-500 text-[14px]">
        Aftercare information will appear here. The studio can add it via the admin panel.
      </p>
    );
  }
  return (
    <div className="space-y-10 max-w-3xl">
      {sections.map((sec, si) => (
        <section key={si} data-testid={`aftercare-section-${si}`}>
          <h2 className="text-2xl font-bold text-stone-900 mb-4" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
            {sec.title}
          </h2>
          <div className="space-y-5">
            {(sec.groups || []).map((g, gi) => (
              <div
                key={gi}
                className="p-5 rounded-2xl border bg-white"
                style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}
                data-testid={`aftercare-group-${si}-${gi}`}
              >
                <h3 className="font-bold text-stone-800 text-[15px] mb-3">{g.heading}</h3>
                <ul className="space-y-2 text-[14px] text-stone-700 leading-relaxed">
                  {(g.items || []).map((it, ii) => (
                    <li key={ii} className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1.5 shrink-0">•</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function GenericBody({ restaurant, pageType, slug }) {
  const meta = PAGE_META[pageType];
  return (
    <div className="prose prose-stone max-w-2xl">
      <p className="text-[15px] text-stone-700 leading-relaxed">
        {restaurant.name} {meta?.blurb.toLowerCase()} Get in touch via the{" "}
        <Link to={`/r/${slug}/contact`} className="underline font-bold">Contact</Link> page
        or use the Book Now button at the top of every page.
      </p>
      <div className="mt-5 p-5 rounded-2xl bg-stone-50 border border-stone-200">
        <div className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-1">Why book with us</div>
        <ul className="space-y-1.5 text-[14px] text-stone-700">
          <li>✓ 10+ years industry experience</li>
          <li>✓ Locally owned & operated</li>
          <li>✓ 5★ rated by {restaurant.review_count || "hundreds of"} customers</li>
          <li>✓ Online booking, instant confirmation</li>
        </ul>
      </div>
    </div>
  );
}

export default function PaidPage({ pageType }) {
  const { restaurant, slug } = useOutletContext() || {};
  if (!restaurant) return null;
  const meta = PAGE_META[pageType] || { title: pageType, icon: Sparkles, blurb: "" };
  const Icon = meta.icon;
  const isPaid = restaurant.tier === "platinum" || restaurant.is_claimed;

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14" data-testid={`paid-page-${pageType}`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-6 h-6" style={{ color: "var(--tpl-accent, #292524)" }} />
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
          {meta.title}
        </h1>
      </div>
      <p className="text-stone-600 text-[15px] mb-8 max-w-2xl">{meta.blurb}</p>

      {!isPaid ? (
        <div className="p-8 rounded-2xl border-2 border-dashed border-stone-300 bg-stone-50 max-w-2xl">
          <Sparkles className="w-6 h-6 text-amber-500 mb-2" />
          <h2 className="font-bold text-stone-900 text-lg mb-1">Unlock this page</h2>
          <p className="text-[14px] text-stone-600 mb-4">
            This page is part of the Platinum member pack — 10+ premium pages including
            Functions, Catering, Gallery, Reviews, FAQ, Loyalty, Gift Cards, Specials,
            Press & Blog. Owners can claim and customise every section.
          </p>
          <Link
            to={`/claim-business?slug=${slug}`}
            data-testid={`paid-page-claim-${pageType}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-900 text-white text-[13px] font-bold hover:bg-stone-700"
          >
            Claim this listing <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : pageType === "reviews" ? (
        <ReviewsBody restaurant={restaurant} />
      ) : pageType === "faq" ? (
        <FaqBody restaurant={restaurant} />
      ) : pageType === "gallery" ? (
        <GalleryBody restaurant={restaurant} />
      ) : pageType === "specials" ? (
        <SpecialsBody restaurant={restaurant} />
      ) : pageType === "gift-cards" ? (
        <GiftCardsBody restaurant={restaurant} slug={slug} />
      ) : pageType === "loyalty" ? (
        <LoyaltyBody restaurant={restaurant} />
      ) : pageType === "aftercare" ? (
        <AftercareBody restaurant={restaurant} />
      ) : (
        <GenericBody restaurant={restaurant} pageType={pageType} slug={slug} />
      )}
    </div>
  );
}
